// The homepage (AC-UX-8): open or drop a PDF, what the app does, the privacy
// promise, and the recent files kept in this browser.
import { useEffect, useState } from 'react';
import type { RecentFile } from '../recent.ts';
import type { Theme } from '../theme.ts';
import { CloseIcon, DeleteIcon, DocumentIcon, EditIcon, ExportIcon, LockIcon, MoonIcon, MoveIcon, OpenIcon, ShieldIcon, SunIcon, UndoIcon } from './icons.tsx';
import { Logo } from './Logo.tsx';

const FEATURES = [
  { icon: <EditIcon />, title: 'Edit text', text: 'Double-click a line and type. The original font is kept where it can be.' },
  { icon: <MoveIcon />, title: 'Move', text: 'Drag lines and images, one or many at once.' },
  { icon: <DeleteIcon />, title: 'Delete', text: 'Text and images are removed from the file, not just covered.' },
  { icon: <UndoIcon />, title: 'Undo', text: 'Step back and forward through every change.' },
  { icon: <ExportIcon />, title: 'Export', text: 'A clean new copy; your original is never changed.' },
];

function size(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function when(t: number): string {
  const min = Math.round((Date.now() - t) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(t).toLocaleDateString();
}

/** A page picture from IndexedDB, shown through an object URL released when it goes. */
function Thumb(props: { blob?: Blob; encrypted: boolean }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!props.blob) return;
    const u = URL.createObjectURL(props.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [props.blob]);
  return <div className="recent-thumb">{url ? <img src={url} alt="" /> : props.encrypted ? <LockIcon size={28} /> : <DocumentIcon size={28} />}</div>;
}

export function HomePage(props: {
  busy: boolean;
  status: string;
  theme: Theme;
  onToggleTheme: () => void;
  onOpen: (file: File) => void;
  /** the document still open in the editor */
  openName?: string;
  onContinue: () => void;
  recent: RecentFile[];
  recentOn: boolean;
  onOpenRecent: (r: RecentFile) => void;
  onRemoveRecent: (r: RecentFile) => void;
  onClearRecent: () => void;
  onRecentOn: (on: boolean) => void;
  /** a click on the logo (five in a row turn debug mode on, D-45) */
  onLogoClick: () => void;
  debug: boolean;
  onDebug: () => void;
}) {
  return (
    <div className="home" data-testid="homepage">
      <header className="home-bar">
        <h1 className="brand" data-testid="home-logo" onClick={props.onLogoClick}>
          <Logo size={30} />
          <span>FlexPDF</span>
        </h1>
        <span className="spacer" />
        {props.debug && (
          <button data-testid="debug-badge-home" className="debug-badge" title="Debug mode is on: show the log" onClick={props.onDebug}>
            Debug
          </button>
        )}
        <button data-testid="theme-toggle-home" className="btn icon-btn" aria-label={props.theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={props.theme === 'dark' ? 'Light mode' : 'Dark mode'} onClick={props.onToggleTheme}>
          {props.theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
      </header>

      <section className="hero">
        <h2>Edit PDFs right in your browser</h2>
        <p className="hero-sub">Change text, move and delete what is on the page, and export a clean copy. Nothing is uploaded.</p>
        <div data-testid="start-screen" className="dropzone">
          <OpenIcon size={30} />
          <label className={`btn btn-primary btn-large${props.busy ? ' is-disabled' : ''}`}>
            <span>Open PDF</span>
            <input
              data-testid="start-file-input"
              className="visually-hidden"
              type="file"
              accept="application/pdf,.pdf"
              disabled={props.busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) props.onOpen(file);
              }}
            />
          </label>
          <p>or drop a PDF anywhere in this window</p>
          {props.status && <p className="muted small">{props.status}</p>}
        </div>
        {props.openName && (
          <button data-testid="continue" className="continue" onClick={props.onContinue}>
            <EditIcon />
            <span>
              Continue editing <b>{props.openName}</b>
            </span>
          </button>
        )}
      </section>

      <section className="features">
        {FEATURES.map((f) => (
          <div key={f.title} className="feature">
            <div className="feature-icon">{f.icon}</div>
            <h3>{f.title}</h3>
            <p>{f.text}</p>
          </div>
        ))}
      </section>

      <section className="recent" data-testid="recent">
        <div className="recent-head">
          <h3>Recent files</h3>
          <span className="spacer" />
          {props.recent.length > 0 && (
            <button data-testid="recent-clear" className="link-btn" onClick={props.onClearRecent}>
              Clear all
            </button>
          )}
          <label className="switch" title="Recent files are kept in this browser only">
            <input data-testid="recent-toggle" type="checkbox" checked={props.recentOn} onChange={(e) => props.onRecentOn(e.target.checked)} />
            <span>Remember recent files</span>
          </label>
        </div>
        {!props.recentOn ? (
          <p className="muted">Recent files are off. Files you open are not kept.</p>
        ) : props.recent.length === 0 ? (
          <p className="muted">Files you open appear here, kept in this browser only.</p>
        ) : (
          <ul className="recent-grid">
            {props.recent.map((r) => (
              <li key={r.id} className="recent-card" data-testid="recent-item" data-name={r.name}>
                <button className="recent-open" disabled={props.busy} title={`Open ${r.name}`} onClick={() => props.onOpenRecent(r)}>
                  <Thumb blob={r.thumb} encrypted={r.encrypted} />
                  <span className="recent-name">{r.name}</span>
                  <span className="recent-meta">
                    {size(r.size)} · {when(r.openedAt)}
                    {r.encrypted && ' · password'}
                  </span>
                </button>
                <button data-testid="recent-remove" className="recent-remove" aria-label={`Remove ${r.name} from recent files`} title="Remove from recent files" onClick={() => props.onRemoveRecent(r)}>
                  <CloseIcon size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <footer className="home-foot">
        <ShieldIcon size={16} />
        <span>Your files never leave this browser. FlexPDF works offline; recent files are stored only here and can be removed at any time.</span>
      </footer>
    </div>
  );
}
